import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Hls from "hls.js";
import {FaCompress, FaExpand, FaPause, FaPlay, FaRedo, FaVolumeDown, FaVolumeMute, FaVolumeUp} from 'react-icons/fa';
import './liveHlsPlayer.css';

/**
 * LiveHlsPlayer (JavaScript) con controles personalizados para directo
 * - Reproduce HLS (.m3u8) con fallback nativo (Safari/iOS)
 * - Controles estilo YouTube: título arriba, controles abajo
 * - Indicadores de estado: conectando, cargando, en vivo, reintentando, error
 * - Sin marca de tiempo; muestra insignia LIVE
 * - Oculta controles y cursor automáticamente (estilo YouTube)
 * - Soporte para pantalla completa
 *
 * Requisitos:
 *   npm i hls.js react-icons
 *   Añade los estilos del archivo CSS incluido
 *
 * Uso básico:
 *   <LiveHlsPlayer
 *     src="https://servidor/manifest.m3u8"
 *     title="Nombre del canal"
 *     autoPlay
 *     muted
 *   />
 */

const defaultConfig = {
    lowLatencyMode: true,
    backBufferLength: 5,
    liveSyncDurationCount: 3,
    liveMaxLatencyDurationCount: 5,
    enableWorker: true,
    fragLoadingRetryDelay: 1000,
    manifestLoadingRetryDelay: 1000,
};

export default function LiveHlsPlayer({
                                          src,
                                          title,
                                          autoPlay = true,
                                          muted = true,
                                          poster,
                                          className,
                                          hlsConfig,
                                          initialVolume = 1,
                                          liveEdgeThreshold = 3, // segundos de retraso para mostrar "Ir al directo"
                                          showNativeControls = false, // por si quieres alternar
                                          onStatusChange,
                                          controlsTimeout = 3000, // tiempo para ocultar controles en ms
                                          showDebugInfo = false, // mostrar panel de información técnica
                                          enableVlcLink = true, // permitir abrir en VLC
                                          extraBtns = null,
                                          onClose = null,

                                      }) {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const playerRef = useRef(null);
    const timeoutRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(Boolean(autoPlay));
    const [isMuted, setIsMuted] = useState(Boolean(muted));
    const [volume, setVolume] = useState(initialVolume);
    const [status, setStatus] = useState("connecting"); // connecting | buffering | playing | stalled | retrying | error
    const [isLive, setIsLive] = useState(true);
    const [behindLive, setBehindLive] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [controlsVisible, setControlsVisible] = useState(true);
    const [showDebug, setShowDebug] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [debugInfo, setDebugInfo] = useState({
        resolution: 'N/A',
        bitrate: 'N/A',
        latency: 0,
        bufferLength: 0,
        downloadSpeed: 'N/A',
        droppedFrames: 0,
        responseTime: 'N/A',
        lastUpdated: Date.now()
    });
    const [isMobile, setIsMobile] = useState(false);
    const [touchStartTime, setTouchStartTime] = useState(0);
    const debugIntervalRef = useRef(null);

    const setStatusSafe = (s) => {
        setStatus((prev) => (prev === s ? prev : s));
        if (onStatusChange) onStatusChange(s);
    };

    // Helpers ---------------------------------------------------------
    const canUseNativeHls = useMemo(() => {
        const v = document.createElement("video");
        return v.canPlayType && v.canPlayType("application/vnd.apple.mpegurl");
    }, []);

    const getLatency = () => {
        const video = videoRef.current;
        if (!video) return 0;
        try {
            // Con hls.js (si está activo y es live)
            if (hlsRef.current && typeof hlsRef.current.latency === "number") {
                return hlsRef.current.latency;
            }
            // Aproximación nativa: diferencia con el final de la ventana seekable
            if (video.seekable && video.seekable.length) {
                const end = video.seekable.end(video.seekable.length - 1);
                return Math.max(0, end - video.currentTime);
            }
        } catch (_) {
        }
        return 0;
    };

    const checkBehindLive = () => {
        const latency = getLatency();
        const behind = latency > liveEdgeThreshold;
        setBehindLive(behind);
    };

    const goToLive = () => {
        const video = videoRef.current;
        if (!video) return;
        try {
            if (hlsRef.current && typeof hlsRef.current.liveSyncPosition === "number") {
                // Para hls.js: ir a la posición de sincronía
                video.currentTime = hlsRef.current.liveSyncPosition;
            } else if (video.seekable && video.seekable.length) {
                // Nativo: ir al final de la ventana seekable
                const end = video.seekable.end(video.seekable.length - 1);
                video.currentTime = end - 0.1; // pequeño margen
            }
            // relanza reproducción si procede
            video.play && video.play().catch(() => {
            });
        } catch (_) {
        }
    };

    // Funciones de pantalla completa
    const toggleFullscreen = () => {
        const player = playerRef.current;
        console.log('toggleFullscreen', isFullscreen, player, document.fullscreenElement);
        if (!player) return;

        if (!document.fullscreenElement) {
            // Entrar en pantalla completa
            if (player.requestFullscreen) {
                player.requestFullscreen().catch(() => {
                });
            } else if (player.webkitRequestFullscreen) {
                player.webkitRequestFullscreen();
            } else if (player.mozRequestFullScreen) {
                player.mozRequestFullScreen();
            } else if (player.msRequestFullscreen) {
                player.msRequestFullscreen();
            }
        } else {
            // Salir de pantalla completa
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    // Detectar cambios en pantalla completa
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Player init / teardown ------------------------------------------
    useLayoutEffect(() => {
        const video = videoRef.current;
        if (!video || !src) return;

        // Reset estado base
        setStatusSafe("connecting");
        setErrorMsg("");
        setIsLive(true);

        // Limpieza previa si ya hay instancia
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        // Config video
        video.muted = isMuted;
        video.volume = volume;

        // Nativo (Safari/iOS)
        if (canUseNativeHls) {
            video.src = src;
            const onLoadStart = () => setStatusSafe("connecting");
            const onWaiting = () => setStatusSafe("buffering");
            const onPlaying = () => setStatusSafe("playing");
            const onStalled = () => setStatusSafe("stalled");
            const onCanPlay = () => setStatusSafe("playing");
            const onError = () => {
                setStatusSafe("error");
                setErrorMsg("No se pudo reproducir el stream.");
            };
            const onProgress = () => checkBehindLive();
            const onLoadedMetadata = () => {
                if (!document.fullscreenElement) {
                    toggleFullscreen();
                }
            }

            video.addEventListener("loadstart", onLoadStart);
            video.addEventListener("waiting", onWaiting);
            video.addEventListener("playing", onPlaying);
            //video.addEventListener("stalled", onStalled);
            video.addEventListener("canplay", onCanPlay);
            video.addEventListener("error", onError);
            video.addEventListener("progress", onProgress);
            video.addEventListener("timeupdate", onProgress);
            video.addEventListener("loadedmetadata", onLoadedMetadata);

            if (autoPlay) {
                video.play().catch(() => {
                });
            }

            return () => {
                video.removeEventListener("loadstart", onLoadStart);
                video.removeEventListener("waiting", onWaiting);
                video.removeEventListener("playing", onPlaying);
                video.removeEventListener("stalled", onStalled);
                video.removeEventListener("canplay", onCanPlay);
                video.removeEventListener("error", onError);
                video.removeEventListener("progress", onProgress);
                video.removeEventListener("timeupdate", onProgress);
                video.removeEventListener("loadedmetadata", onLoadedMetadata);
                video.removeAttribute("src");
                video.load();
            };
        }

        // hls.js (otros navegadores)
        if (Hls.isSupported()) {
            const hls = new Hls({...defaultConfig, ...(hlsConfig || {})});
            hlsRef.current = hls;

            hls.on(Hls.Events.START, () => {
                if (!document.fullscreenElement) {
                    toggleFullscreen();
                }
            })

            hls.attachMedia(video);
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                hls.loadSource(src);
            });

            hls.on(Hls.Events.MANIFEST_LOADING, () => setStatusSafe("connecting"));
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setStatusSafe("buffering");
                if (autoPlay) video.play().catch(() => {
                });
            });
            hls.on(Hls.Events.LEVEL_LOADED, (_e, data) => {
                // Detectar si es live
                setIsLive(Boolean(data?.details?.live));
                checkBehindLive();
            });
            hls.on(Hls.Events.FRAG_BUFFERED, () => {
                setStatusSafe("playing");
                checkBehindLive();
            });
            hls.on(Hls.Events.BUFFER_STALLED, () => setStatusSafe("stalled"));

            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            setStatusSafe("retrying");
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            setStatusSafe("retrying");
                            hls.recoverMediaError();
                            break;
                        default:
                            setStatusSafe("error");
                            setErrorMsg(data?.details || "Error desconocido");
                            hls.destroy();
                            // Intento de recreación simple
                            setTimeout(() => {
                                if (!videoRef.current) return;
                                const retry = new Hls({...defaultConfig, ...(hlsConfig || {})});
                                hlsRef.current = retry;
                                retry.attachMedia(videoRef.current);
                                retry.on(Hls.Events.MEDIA_ATTACHED, () => retry.loadSource(src));
                            }, 1000);
                            break;
                    }
                }
            });

            const onProgress = () => checkBehindLive();
            video.addEventListener("timeupdate", onProgress);
            video.addEventListener("progress", onProgress);

            return () => {
                video.removeEventListener("timeupdate", onProgress);
                video.removeEventListener("progress", onProgress);
                hls.destroy();
            };
        }

        console.warn("HLS no soportado por este navegador.");
    }, [src]);

    // Manejo de visibilidad de controles estilo YouTube
    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;

        // Función para mostrar controles
        const showControls = () => {
            setControlsVisible(true);
            // Resetear el temporizador cada vez que hay movimiento
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            // Establecer un nuevo temporizador
            timeoutRef.current = setTimeout(() => {
                // Solo ocultamos si está reproduciendo
                if (isPlaying) {
                    setControlsVisible(false);
                }
            }, isMobile ? controlsTimeout * 1.5 : controlsTimeout); // Más tiempo en móviles
        };

        // Eventos para rastrear actividad del usuario
        const handleMouseMove = () => showControls();
        const handleMouseLeave = () => {
            if (isPlaying) {
                setControlsVisible(false);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            }
        };
        const handleMouseEnter = () => showControls();

        // Eventos táctiles para dispositivos móviles
        const handleTouchStart = (e) => {
            setTouchStartTime(Date.now());
            showControls();
        };

        const handleTouchEnd = (e) => {
            const touchDuration = Date.now() - touchStartTime;
            // Solo consideramos como tap si es menos de 300ms
            if (touchDuration < 300) {
                // Si fue un tap corto, alternar reproducción
                togglePlay();
            }
        };

        // Aplicar eventos
        player.addEventListener('mousemove', handleMouseMove);
        player.addEventListener('mouseleave', handleMouseLeave);
        player.addEventListener('mouseenter', handleMouseEnter);
        player.addEventListener('touchstart', handleTouchStart, {passive: true});
        player.addEventListener('touchend', handleTouchEnd);

        // Limpiar eventos y timeouts
        return () => {
            player.removeEventListener('mousemove', handleMouseMove);
            player.removeEventListener('mouseleave', handleMouseLeave);
            player.removeEventListener('mouseenter', handleMouseEnter);
            player.removeEventListener('touchstart', handleTouchStart);
            player.removeEventListener('touchend', handleTouchEnd);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [controlsTimeout, isPlaying, touchStartTime]);

    // Efecto para mostrar controles cuando se pausa el video
    useEffect(() => {
        if (!isPlaying) {
            setControlsVisible(true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        } else if (!controlsVisible) {
            // Si estaba oculto y ahora está reproduciendo, dejarlo oculto
        } else {
            // Si estaba visible y ahora está reproduciendo, iniciar temporizador para ocultar
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                setControlsVisible(false);
            }, controlsTimeout);
        }
    }, [isPlaying, controlsTimeout]);

    // Recolección de datos para el debug
    useEffect(() => {
        if (!showDebug) {
            if (debugIntervalRef.current) {
                clearInterval(debugIntervalRef.current);
                debugIntervalRef.current = null;
            }
            return;
        }

        const updateDebugInfo = () => {
            const video = videoRef.current;
            const hls = hlsRef.current;
            if (!video) return;

            let newDebugInfo = {...debugInfo};

            // Básicos del vídeo
            if (video.videoHeight && video.videoWidth) {
                newDebugInfo.resolution = `${video.videoWidth}x${video.videoHeight}`;
            }

            // Info de HLS.js
            if (hls) {
                // Latencia
                if (typeof hls.latency === 'number') {
                    newDebugInfo.latency = hls.latency.toFixed(2) + 's';
                }

                // Nivel actual y bitrate
                const currentLevel = hls.currentLevel;
                if (currentLevel >= 0 && hls.levels && hls.levels[currentLevel]) {
                    const bitrateKbps = Math.round(hls.levels[currentLevel].bitrate / 1000);
                    newDebugInfo.bitrate = `${bitrateKbps} kbps`;
                }

                // Frames descartados
                if (video.webkitDroppedFrameCount !== undefined) {
                    newDebugInfo.droppedFrames = video.webkitDroppedFrameCount;
                }

                // Estadísticas de red
                if (hls.stats) {
                    // Tiempo de respuesta del servidor
                    if (hls.stats.fragLoadingMedian) {
                        newDebugInfo.responseTime = hls.stats.fragLoadingMedian.toFixed(2) + 'ms';
                    }

                    // Velocidad de descarga
                    if (hls.stats.fragDecodedMedian) {
                        const downloadMbps = (hls.stats.fragDecodedMedian / 1000 / 8).toFixed(2);
                        newDebugInfo.downloadSpeed = `${downloadMbps} MB/s`;
                    }

                    // Longitud del buffer
                    if (video.buffered && video.buffered.length) {
                        const bufferEnd = video.buffered.end(video.buffered.length - 1);
                        const bufferLength = bufferEnd - video.currentTime;
                        newDebugInfo.bufferLength = bufferLength.toFixed(2) + 's';
                    }
                }
            } else {
                // Fallback para navegadores con HLS nativo
                if (video.buffered && video.buffered.length) {
                    const bufferEnd = video.buffered.end(video.buffered.length - 1);
                    const bufferLength = bufferEnd - video.currentTime;
                    newDebugInfo.bufferLength = bufferLength.toFixed(2) + 's';

                    // Cálculo básico de latencia para HLS nativo
                    const latency = getLatency();
                    if (latency) {
                        newDebugInfo.latency = latency.toFixed(2) + 's';
                    }
                }
            }

            newDebugInfo.lastUpdated = Date.now();
            setDebugInfo(newDebugInfo);
        };

        // Actualizar cada 1 segundo
        debugIntervalRef.current = setInterval(updateDebugInfo, 1000);

        return () => {
            if (debugIntervalRef.current) {
                clearInterval(debugIntervalRef.current);
            }
        };
    }, [showDebug]);

    // Toggle para mostrar/ocultar el panel de debug
    const toggleDebugPanel = () => {
        setShowDebug(prev => !prev);
    };

    // Controles -------------------------------------------------------
    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play().then(() => setIsPlaying(true)).catch(() => {
            });
        } else {
            v.pause();
            setIsPlaying(false);
        }
    };

    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setIsMuted(v.muted);
    };

    const onVolumeChange = (e) => {
        const v = videoRef.current;
        if (!v) return;
        const vol = Number(e.target.value);
        v.volume = vol;
        setVolume(vol);
        if (v.muted && vol > 0) {
            v.muted = false;
            setIsMuted(false);
        }
    };

    // Sincronizar flags desde eventos nativos (por si cambian fuera de los botones)
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onMuteChange = () => setIsMuted(v.muted);
        v.addEventListener("play", onPlay);
        v.addEventListener("pause", onPause);
        v.addEventListener("volumechange", onMuteChange);
        return () => {
            v.removeEventListener("play", onPlay);
            v.removeEventListener("pause", onPause);
            v.removeEventListener("volumechange", onMuteChange);
        };
    }, []);

    // Detectar si es un dispositivo móvil
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        // Comprobar al inicio
        checkMobile();

        // Comprobar en cada cambio de tamaño
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // UI --------------------------------------------------------------
    const statusLabel = useMemo(() => {
        const mapping = {
            connecting: "Conectando…",
            buffering: "Cargando…",
            playing: "En vivo",
            stalled: "Reconectando…",
            retrying: "Reintentando…",
            error: "Error",
        };
        return mapping[status] || "";
    }, [status]);

    // Función para determinar qué ícono de volumen mostrar
    const getVolumeIcon = () => {
        if (isMuted || volume === 0) {
            return <FaVolumeMute/>;
        } else if (volume < 0.5) {
            return <FaVolumeDown/>;
        }
        return <FaVolumeUp/>;
    };

    return (
        <div
            className={`live-hls-player ${className || ""} ${!controlsVisible ? 'is-cursor-hidden' : ''} ${isMobile ? 'is-mobile' : ''}`}
            ref={playerRef}>
            <div className="live-hls-player__stage">
                <video
                    ref={videoRef}
                    className="live-hls-player__video"
                    poster={poster}
                    muted={isMuted}
                    playsInline
                    autoPlay={autoPlay}
                    controls={showNativeControls}
                    aria-label={title || "Reproductor en directo"}
                    onClick={togglePlay}
                />

                {/* Overlay de estado */}
                <div className={`live-hls-player__overlay ${status !== "playing" ? "is-visible" : ""}`}>
                    {status !== "playing" && (
                        <div className="live-hls-player__status">
                            <span className={`dot dot--${status}`}/>
                            <span>{statusLabel}</span>
                            {status === "error" && errorMsg ? (
                                <span className="live-hls-player__error">{errorMsg}</span>
                            ) : null}
                        </div>
                    )}
                </div>

                {/* Controles superiores (título y badge) */}
                <div className={`live-hls-player__top-controls ${!controlsVisible ? 'is-hidden' : ''}`}>
                    <div className="live-hls-player__title-container">
                        {onClose && (
                            <button className="hlsbtn" onClick={onClose}><i
                                className="fa-solid fa-xmark"></i>
                            </button>
                        )}
                        {title ? (
                            <div className="live-hls-player__title" title={title} aria-label={`Emisión: ${title}`}>
                                {title}
                            </div>
                        ) : null}

                        {/* Badge LIVE */}
                        {isLive && (
                            <div className={`live-hls-player__live ${behindLive ? "is-behind" : ""}`}
                                 aria-live="polite">
                                <span className="live-dot" aria-hidden="true"/>
                                <span>EN VIVO</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Controles inferiores */}
                <div className={`live-hls-player__bottom-controls ${!controlsVisible ? 'is-hidden' : ''}`}
                     role="group" aria-label="Controles de reproducción">

                    {/* Play/Pause */}
                    <button className="hlsbtn" onClick={togglePlay} aria-label={isPlaying ? "Pausar" : "Reproducir"}>
                        {isPlaying ? <FaPause/> : <FaPlay/>}
                    </button>

                    {!!extraBtns?.length && extraBtns.map((btn, index) => (
                        <button className="hlsbtn" title={btn.title} onClick={btn.onClick} key={btn.title}>
                            {btn.label}
                        </button>))}

                    {/* Botón Ir al directo cuando está retrasado */}
                    {behindLive && (
                        <button className="hlsbtn hlsbtn--live-edge" onClick={goToLive}>
                            <FaRedo/>
                            <span>Volver al directo</span>
                        </button>
                    )}

                    <div className="live-hls-player__spacer"/>

                    {/* Botón para mostrar/ocultar panel de debug */}
                    {showDebugInfo &&
                        <button className={`hlsbtn ${showDebug ? 'hlsbtn--active' : ''}`} onClick={toggleDebugPanel}
                                aria-label="Mostrar información técnica">
                            <svg className="hlsbtn-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M20 8h-2.81c-.45-.78-1.07-1.45-1.82-1.96L17 4.41 15.59 3l-2.17 2.17C12.96 5.06 12.49 5 12 5s-.96.06-1.41.17L8.41 3 7 4.41l1.62 1.63C7.88 6.55 7.26 7.22 6.81 8H4v2h2.09c-.05.33-.09.66-.09 1v1H4v2h2v1c0 .34.04.67.09 1H4v2h2.81c1.04 1.79 2.97 3 5.19 3s4.15-1.21 5.19-3H20v-2h-2.09c.05-.33.09-.66.09-1v-1h2v-2h-2v-1c0-.34-.04-.67-.09-1H20V8zm-4 4v3c0 .22-.03.47-.07.7l-.1.65-.37.65c-.72 1.24-2.04 2-3.46 2s-2.74-.77-3.46-2l-.37-.64-.1-.65C8.03 15.48 8 15.23 8 15v-4c0-.23.03-.48.07-.7l.1-.65.37-.65c.3-.52.72-.97 1.21-1.31l.57-.39.74-.18c.31-.08.63-.12.94-.12.32 0 .63.04.95.12l.68.16.61.42c.5.34.91.78 1.21 1.31l.38.65.1.65c.04.22.07.47.07.69v1zm-6 2h4v2h-4v-2zm0-4h4v2h-4v-2z"/>
                            </svg>
                        </button>}

                    {/* Volumen con slider oculto - Ocultar en móviles pequeños */}
                    {(!isMobile || window.innerWidth > 480) && (
                        <div className="vol-container">
                            <button className="hlsbtn" onClick={toggleMute}
                                    aria-label={isMuted ? "Activar sonido" : "Silenciar"}>
                                {getVolumeIcon()}
                            </button>
                            <div className="vol-range-container">
                                <label className="sr-only" htmlFor="vol-range">Volumen</label>
                                <input
                                    id="vol-range"
                                    className="vol-range"
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={volume}
                                    onChange={onVolumeChange}
                                    aria-valuemin={0}
                                    aria-valuemax={1}
                                    aria-valuenow={volume}
                                    aria-label="Volumen"
                                />
                            </div>
                        </div>
                    )}

                    {/* Solo mostrar botón de mute en móviles pequeños, sin slider */}
                    {isMobile && window.innerWidth <= 480 && (
                        <button className="hlsbtn" onClick={toggleMute}
                                aria-label={isMuted ? "Activar sonido" : "Silenciar"}>
                            {getVolumeIcon()}
                        </button>
                    )}

                    {/* Botón de pantalla completa */}
                    <button className="hlsbtn" onClick={toggleFullscreen}
                            aria-label={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}>
                        {isFullscreen ? <FaCompress/> : <FaExpand/>}
                    </button>
                </div>
            </div>

            {/* Indicador central de play/pause */}
            <div
                className={`live-hls-player__center-indicator ${isPlaying ? 'is-playing' : 'is-paused'} ${controlsVisible ? 'is-visible' : ''}`}>
                <div className="indicator-icon"></div>
            </div>

            {/* Panel de información técnica (debug) */}
            {showDebug && (
                <div className="live-hls-player__debug-panel">
                    <h4>Información técnica</h4>
                    <table>
                        <tbody>
                        <tr>
                            <td>Resolución:</td>
                            <td>{debugInfo.resolution}</td>
                        </tr>
                        <tr>
                            <td>Bitrate:</td>
                            <td>{debugInfo.bitrate}</td>
                        </tr>
                        <tr>
                            <td>Latencia:</td>
                            <td>{debugInfo.latency}</td>
                        </tr>
                        <tr>
                            <td>Buffer:</td>
                            <td>{debugInfo.bufferLength}</td>
                        </tr>
                        <tr>
                            <td>Velocidad:</td>
                            <td>{debugInfo.downloadSpeed}</td>
                        </tr>
                        <tr>
                            <td>Frames perdidos:</td>
                            <td>{debugInfo.droppedFrames}</td>
                        </tr>
                        <tr>
                            <td>Tiempo respuesta:</td>
                            <td>{debugInfo.responseTime}</td>
                        </tr>
                        <tr>
                            <td>Estado:</td>
                            <td>{status}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
